import { isActiveCrossAssetRiskGroup } from '@src/components/AutoHedging/crossAssetRiskGroup';
import { getCrossAssetRiskGroupAssetsDisplayValue } from '@src/containers/Admin/TradingLimits/CrossAssetRiskGroups/useCrossAssetRiskGroupColumns';
import { useGetCrossAssetRiskGroupsQuery } from '@src/containers/Admin/TradingLimits/SubAccountTradingLimits/crossAssetRiskGroupsApi';
import { getCrossAssetRiskGroupLabel } from '@src/containers/Admin/TradingLimits/SubAccountTradingLimits/matchTargetUtils';
import { useFeatureFlag } from '@src/hooks';
import { ACTION, AgGridLinesTooltip, IconName, useAssetsContext, type ColumnDef, type IHedgeRule } from '@talos/kyoko';
import type { IRowNode, ValueFormatterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { usePositionAutoHedgingRulesDrawer } from '../../../../components/AutoHedgingRulesDrawer/AutoHedgingRulesDrawer';
import { useRoleAuth, useRollupTreeRef } from '../../../../hooks';
import { useDeleteHedgeRule } from './useDeleteHedgeRule';

export const useHedgeRuleControlsColumns = (): ColumnDef<IHedgeRule>[] => {
  const { isAuthorized } = useRoleAuth();
  const { deleteHedgeRule } = useDeleteHedgeRule();
  const { openPositionAutoHedgingRulesDrawer } = usePositionAutoHedgingRulesDrawer();
  const rollupTreeRef = useRollupTreeRef();
  const { getAssetDisplaySymbol } = useAssetsContext();
  const { enableHedgeRuleCrossAssetRiskGroup, enableCrossAssetRiskGroupDeltaRiskCheck } = useFeatureFlag();
  const { data: crossAssetRiskGroups } = useGetCrossAssetRiskGroupsQuery(undefined, {
    skip: !enableHedgeRuleCrossAssetRiskGroup,
  });
  const crossAssetRiskGroupsByName = useMemo(
    () => new Map((crossAssetRiskGroups ?? []).map(group => [group.Name, group])),
    [crossAssetRiskGroups]
  );

  return useMemo(
    () =>
      [
        {
          field: 'Mode',
          type: 'connectionStatus',
          title: 'Enabled',
          width: 65,
        },
        {
          field: 'AccountID',
          title: 'Monitored Rollup',
          type: 'subAccountName',
          params: {
            rollupTree: rollupTreeRef,
          },
        },
        {
          field: 'Asset',
          title: 'Monitored Asset',
          type: 'asset',
        },
        ...(enableHedgeRuleCrossAssetRiskGroup
          ? [
              {
                id: 'CrossAssetRiskGroup',
                field: 'CrossAssetRiskGroup',
                title: 'Cross-Asset Risk Group',
                type: 'custom',
                params: {
                  valueFormatter: ({ value }: ValueFormatterParams<IHedgeRule, string>) => {
                    if (!isActiveCrossAssetRiskGroup(value)) {
                      return '';
                    }
                    return getCrossAssetRiskGroupLabel(crossAssetRiskGroupsByName.get(value)) || value || '';
                  },
                  tooltipValueGetter: ({ data }: { data?: IHedgeRule }) => {
                    const group =
                      data && isActiveCrossAssetRiskGroup(data.CrossAssetRiskGroup)
                        ? crossAssetRiskGroupsByName.get(data.CrossAssetRiskGroup)
                        : undefined;
                    const assets = group
                      ? getCrossAssetRiskGroupAssetsDisplayValue(
                          group.Assets,
                          getAssetDisplaySymbol,
                          enableCrossAssetRiskGroupDeltaRiskCheck
                        )
                      : [];
                    return assets.length > 0 ? assets : null;
                  },
                  tooltipComponent: AgGridLinesTooltip,
                },
              } satisfies ColumnDef<IHedgeRule>,
            ]
          : []),
        {
          field: 'HedgeSymbol',
          title: 'Hedge Security',
          type: 'security',
        },
        {
          field: 'TargetAsset',
          type: 'currency',
        },
        {
          field: 'ShortPositionLimit',
          title: 'Short Threshold',
          type: 'number',
          params: { currencyField: 'TargetAsset' },
        },
        {
          field: 'TargetPosition',
          type: 'number',
          params: {
            currencyField: 'TargetAsset',
          },
        },
        {
          field: 'LongPositionLimit',
          title: 'Long Threshold',
          type: 'number',
          params: { currencyField: 'TargetAsset' },
        },
        {
          field: 'TargetAccountID',
          title: 'Hedge Account',
          type: 'subAccount',
        },
        {
          field: 'HedgeStrategy',
          type: 'strategy',
        },
        {
          field: 'HedgeAggregation',
          type: 'pricingAggregation',
        },
        { field: 'HedgeRuleID', type: 'id', hide: true, params: { maxLength: 6 } },
        { field: 'Timestamp', type: 'text', hide: true },
        { field: 'OrgID', type: 'text', hide: true },
        { field: 'UpdateAction', type: 'text', hide: true },
        { field: 'Revision', type: 'text', hide: true },
        { field: 'UpdateUser', type: 'text', hide: true },
        { field: 'Priority', type: 'text', hide: true },
        {
          id: 'edit',
          type: 'iconButton',
          pinned: 'right',
          width: 60,
          suppressColumnsToolPanel: true,
          params: {
            onClick: ({ node }: { node: IRowNode<IHedgeRule> }) => {
              if (!node.data) {
                return;
              }
              openPositionAutoHedgingRulesDrawer({ hedgeRuleID: node.data.HedgeRuleID });
            },
            icon: IconName.Pencil,
          },
        },
        ...(isAuthorized(ACTION.EDIT_AUTOHEDGING)
          ? ([
              {
                id: 'delete',
                type: 'iconButton',
                pinned: 'right',
                width: 45,
                suppressColumnsToolPanel: true,
                params: {
                  onClick: ({ node }: { node: IRowNode<IHedgeRule> }) => {
                    if (!node.data) {
                      return;
                    }
                    deleteHedgeRule(node.data);
                  },
                  icon: IconName.Trash,
                  title: 'Delete Hedge Rule',
                },
              },
            ] satisfies ColumnDef<IHedgeRule>[])
          : []),
      ] satisfies ColumnDef<IHedgeRule>[],
    [
      crossAssetRiskGroupsByName,
      enableCrossAssetRiskGroupDeltaRiskCheck,
      enableHedgeRuleCrossAssetRiskGroup,
      deleteHedgeRule,
      getAssetDisplaySymbol,
      isAuthorized,
      openPositionAutoHedgingRulesDrawer,
      rollupTreeRef,
    ]
  );
};
